/**
 * Movement & Posture Records — wellness tenants.
 *
 * Sections:
 *  • Hero with "Nuova sessione" CTA
 *  • Grid of past sessions with thumbnail mosaic + assessment badge
 *  • Click session → expanded view with photo carousel
 *  • Before/After slider: pick 2 sessions, drag a divider on top of overlapped photos
 *  • Future-ready: ai_summary / ai_progress_analysis / ai_posture_score fields
 *    already persisted on each session, no AI implementation in Phase B.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API, BACKEND_URL } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Camera, Upload, X, Loader2, Plus, Trash2, Sparkles, GitCompare,
  ImageIcon, Calendar, Eye, Layers, ChevronLeft, ChevronRight,
} from "lucide-react";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const ASSESS_META = {
  initial:   { label: "Iniziale",  cls: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300" },
  follow_up: { label: "Follow-up", cls: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300" },
  discharge: { label: "Dimissione",cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" },
};

const PostureTab = ({ clientId }) => {
  const [sessions, setSessions] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [openNew,  setOpenNew]  = useState(false);
  const [zoom,     setZoom]     = useState(null);    // session being viewed
  const [compare,  setCompare]  = useState(null);    // {a, b} for slider

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/wellness/posture/sessions/${clientId}`, { withCredentials: true });
      setSessions(r.data.sessions || []);
    } catch {
      toast.error("Errore caricamento posture");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const pickCompare = () => {
    if (sessions.length < 2) {
      toast.error("Servono almeno 2 sessioni per confrontare");
      return;
    }
    setCompare({ a: sessions[sessions.length - 1], b: sessions[0] }); // oldest vs newest
  };

  return (
    <div className="space-y-4" data-testid="posture-tab">
      <div className="rounded-3xl border border-slate-200/60 dark:border-white/10 bg-gradient-to-br from-blue-500/5 via-transparent to-cyan-500/5 dark:from-blue-500/10 dark:to-cyan-500/10 p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow">
              <Camera className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-blue-500">Movement & Posture</p>
              <h3 className="text-lg sm:text-xl font-black text-slate-900 dark:text-white tracking-tight">Documentazione visiva</h3>
              <p className="text-[11px] text-slate-500 mt-0.5">{sessions.length} {sessions.length === 1 ? "sessione" : "sessioni"} · confronta l'evoluzione del paziente</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {sessions.length >= 2 && (
              <Button
                onClick={pickCompare}
                variant="outline"
                className="rounded-full"
                data-testid="posture-compare-btn"
              >
                <GitCompare className="w-4 h-4 mr-1.5" /> Confronta
              </Button>
            )}
            <Button
              onClick={() => setOpenNew(true)}
              className="rounded-full bg-gradient-to-r from-blue-600 to-cyan-500 text-white"
              data-testid="posture-new-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Nuova sessione
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[0,1].map(i => <div key={i} className="h-44 rounded-2xl bg-slate-100 dark:bg-white/5 animate-pulse" />)}
        </div>
      ) : sessions.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 dark:border-white/10 p-10 text-center" data-testid="posture-empty">
          <div className="mx-auto w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-100 to-cyan-100 dark:from-blue-900/30 dark:to-cyan-900/30 flex items-center justify-center mb-2">
            <ImageIcon className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Nessuna foto ancora</p>
          <p className="text-xs text-slate-400 mt-1">Crea la prima sessione posturale per iniziare a tracciare visivamente il recupero.</p>
        </div>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="posture-sessions-grid">
          {sessions.map(s => <SessionCard key={s.session_id} s={s} onOpen={() => setZoom(s)} onChanged={load} />)}
        </ul>
      )}

      <NewSessionDialog open={openNew} onOpenChange={setOpenNew} clientId={clientId} onCreated={() => { setOpenNew(false); load(); }} />
      {zoom && <SessionZoom session={zoom} onClose={() => setZoom(null)} onChanged={() => { setZoom(null); load(); }} />}
      {compare && (
        <CompareDialog
          sessions={sessions}
          initial={compare}
          onClose={() => setCompare(null)}
        />
      )}
    </div>
  );
};

/* ============================================================ */
/* SESSION CARD                                                 */
/* ============================================================ */
const SessionCard = ({ s, onOpen, onChanged }) => {
  const meta = ASSESS_META[s.assessment_type] || ASSESS_META.follow_up;
  const photos = s.photos || [];
  const [busy, setBusy] = useState(false);

  const remove = async (e) => {
    e.stopPropagation();
    if (!window.confirm("Eliminare la sessione e tutte le foto?")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/wellness/posture/sessions/${s.session_id}`, { withCredentials: true });
      toast.success("Sessione eliminata");
      onChanged();
    } catch {
      toast.error("Errore eliminazione");
    } finally {
      setBusy(false);
    }
  };

  return (
    <li
      className="group rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/40 overflow-hidden hover:border-blue-300 transition-all cursor-pointer"
      onClick={onOpen}
      data-testid={`posture-session-${s.session_id}`}
    >
      {/* Photo mosaic */}
      <div className="grid grid-cols-2 gap-px bg-slate-100 dark:bg-slate-800 aspect-[3/2]">
        {photos.slice(0, 4).map(ph => (
          <img
            key={ph.photo_id}
            src={`${BACKEND_URL}${ph.url}`}
            alt={ph.view_label}
            className="object-cover w-full h-full"
            crossOrigin="use-credentials"
          />
        ))}
        {photos.length === 0 && (
          <div className="col-span-2 row-span-2 flex items-center justify-center text-slate-300">
            <ImageIcon className="w-10 h-10" />
          </div>
        )}
        {photos.length > 4 && (
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-2 text-white text-xs font-bold">
            +{photos.length - 4} foto
          </div>
        )}
      </div>

      <div className="p-3">
        <div className="flex items-center justify-between gap-2">
          <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${meta.cls}`}>
            {meta.label}
          </span>
          <span className="text-[10px] text-slate-400 inline-flex items-center gap-1">
            <Calendar className="w-3 h-3" /> {fmtDate(s.captured_at)}
          </span>
        </div>
        <p className="text-sm font-bold text-slate-900 dark:text-white mt-1 truncate">{s.title}</p>
        {s.note && <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{s.note}</p>}
        <div className="mt-2 flex items-center justify-between">
          <span className="text-[10px] font-bold text-slate-400">{photos.length} foto</span>
          <button onClick={remove} disabled={busy} className="opacity-0 group-hover:opacity-100 transition-opacity text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-full p-1">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    </li>
  );
};

/* ============================================================ */
/* NEW SESSION DIALOG                                           */
/* ============================================================ */
const NewSessionDialog = ({ open, onOpenChange, clientId, onCreated }) => {
  const [photos, setPhotos]   = useState([]);   // { dataUrl, view, file }
  const [title,  setTitle]    = useState("");
  const [type,   setType]     = useState("follow_up");
  const [note,   setNote]     = useState("");
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    if (open) { setPhotos([]); setTitle(""); setType("follow_up"); setNote(""); }
  }, [open]);

  const addFiles = (files) => {
    const arr = Array.from(files || []).slice(0, 8 - photos.length);
    arr.forEach(f => {
      if (f.size > 15 * 1024 * 1024) { toast.error(`${f.name}: file troppo grande (>15MB)`); return; }
      const reader = new FileReader();
      reader.onload = () => setPhotos(p => [...p, { dataUrl: reader.result, view: "front", file: f }]);
      reader.readAsDataURL(f);
    });
  };

  const removeAt = (i) => setPhotos(p => p.filter((_, idx) => idx !== i));
  const setView = (i, v) => setPhotos(p => p.map((ph, idx) => idx === i ? { ...ph, view: v } : ph));

  const submit = async () => {
    if (photos.length === 0) { toast.error("Aggiungi almeno una foto"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/wellness/posture/sessions/${clientId}`, {
        assessment_type: type,
        title: title.trim() || null,
        note:  note.trim() || null,
        photos: photos.map(p => ({ view: p.view, data_url: p.dataUrl })),
      }, { withCredentials: true });
      toast.success("Sessione salvata");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="posture-new-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
              <Camera className="w-3.5 h-3.5 text-white" />
            </div>
            Nuova sessione posturale
          </DialogTitle>
          <DialogDescription>Carica fino a 8 foto. Indica la vista per ciascuna (anteriore, posteriore, lato).</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Tipo valutazione</label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="posture-new-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="initial">Valutazione iniziale</SelectItem>
                <SelectItem value="follow_up">Controllo intermedio</SelectItem>
                <SelectItem value="discharge">Valutazione finale</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Titolo (opzionale)</label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Es: Dopo 4 sedute" data-testid="posture-new-title" />
          </div>
        </div>

        <div>
          <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-slate-300 dark:border-white/10 rounded-2xl cursor-pointer hover:border-blue-400 transition-all">
            <Upload className="w-6 h-6 text-slate-400 mb-1" />
            <span className="text-xs font-bold text-slate-600 dark:text-slate-300">Trascina o tocca per caricare foto</span>
            <span className="text-[10px] text-slate-400">JPG · PNG · WEBP · HEIC · max 15MB</span>
            <input
              type="file" multiple accept="image/*"
              className="sr-only"
              onChange={e => addFiles(e.target.files)}
              data-testid="posture-new-files"
            />
          </label>
        </div>

        {photos.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-64 overflow-y-auto" data-testid="posture-new-thumbs">
            {photos.map((p, i) => (
              <div key={i} className="relative rounded-xl overflow-hidden border border-slate-200 dark:border-white/10">
                <img src={p.dataUrl} alt="" className="w-full h-24 object-cover" />
                <button onClick={() => removeAt(i)} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-rose-500">
                  <X className="w-3 h-3" />
                </button>
                <Select value={p.view} onValueChange={(v) => setView(i, v)}>
                  <SelectTrigger className="rounded-none border-0 h-6 text-[10px] bg-slate-50 dark:bg-slate-800" data-testid={`posture-new-view-${i}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="front">Frontale</SelectItem>
                    <SelectItem value="back">Posteriore</SelectItem>
                    <SelectItem value="left">Sinistro</SelectItem>
                    <SelectItem value="right">Destro</SelectItem>
                    <SelectItem value="other">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        )}

        <Textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Note cliniche (opzionali)" className="text-sm min-h-[60px]" data-testid="posture-new-note" />

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Annulla</Button>
          <Button
            onClick={submit}
            disabled={saving || photos.length === 0}
            className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white"
            data-testid="posture-new-confirm"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4 mr-1" />}
            Salva sessione
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ============================================================ */
/* SESSION ZOOM (carousel)                                      */
/* ============================================================ */
const SessionZoom = ({ session, onClose }) => {
  const [idx, setIdx] = useState(0);
  const photos = session.photos || [];
  if (photos.length === 0) return null;
  const p = photos[idx];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden" data-testid="posture-zoom">
        <div className="bg-black aspect-video flex items-center justify-center relative">
          <img src={`${BACKEND_URL}${p.url}`} alt={p.view_label} className="max-h-[60vh] object-contain" crossOrigin="use-credentials" />
          {photos.length > 1 && (
            <>
              <button onClick={() => setIdx((idx - 1 + photos.length) % photos.length)} className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center backdrop-blur">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button onClick={() => setIdx((idx + 1) % photos.length)} className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center backdrop-blur">
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
          <span className="absolute top-2 left-2 text-xs font-bold uppercase tracking-wider text-white/80 px-2 py-0.5 rounded-full bg-black/40">
            {p.view_label} · {idx + 1}/{photos.length}
          </span>
        </div>
        <div className="p-4">
          <h3 className="font-bold text-slate-900 dark:text-white">{session.title}</h3>
          <p className="text-xs text-slate-500 mt-1">{fmtDate(session.captured_at)}</p>
          {session.note && <p className="text-sm text-slate-700 dark:text-slate-300 mt-2">{session.note}</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ============================================================ */
/* COMPARE DIALOG (before/after slider)                         */
/* ============================================================ */
const CompareDialog = ({ sessions, initial, onClose }) => {
  const [a, setA] = useState(initial.a);
  const [b, setB] = useState(initial.b);
  const [view, setView] = useState("front");

  const photoA = useMemo(() => (a.photos || []).find(p => p.view === view) || a.photos?.[0], [a, view]);
  const photoB = useMemo(() => (b.photos || []).find(p => p.view === view) || b.photos?.[0], [b, view]);

  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);

  const onMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
    setPos((x / rect.width) * 100);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="posture-compare-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitCompare className="w-4 h-4 text-cyan-500" /> Confronto evoluzione
          </DialogTitle>
          <DialogDescription>Trascina il cursore per confrontare prima e dopo</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2 mb-2">
          <Select value={a.session_id} onValueChange={(v) => setA(sessions.find(s => s.session_id === v))}>
            <SelectTrigger data-testid="compare-pick-a"><SelectValue /></SelectTrigger>
            <SelectContent>
              {sessions.map(s => (
                <SelectItem key={s.session_id} value={s.session_id}>
                  {fmtDate(s.captured_at)} · {s.title.slice(0, 30)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={b.session_id} onValueChange={(v) => setB(sessions.find(s => s.session_id === v))}>
            <SelectTrigger data-testid="compare-pick-b"><SelectValue /></SelectTrigger>
            <SelectContent>
              {sessions.map(s => (
                <SelectItem key={s.session_id} value={s.session_id}>
                  {fmtDate(s.captured_at)} · {s.title.slice(0, 30)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex justify-center gap-1 mb-2">
          {["front", "back", "left", "right"].map(v => (
            <button key={v} onClick={() => setView(v)}
              className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${view === v ? "bg-slate-900 text-white" : "bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-300"}`}
              data-testid={`compare-view-${v}`}>
              {v}
            </button>
          ))}
        </div>

        {photoA && photoB ? (
          <div
            ref={containerRef}
            className="relative aspect-video bg-black rounded-xl overflow-hidden select-none"
            onMouseMove={e => e.buttons === 1 && onMove(e.clientX)}
            onTouchMove={e => onMove(e.touches[0].clientX)}
            data-testid="compare-slider"
          >
            <img src={`${BACKEND_URL}${photoB.url}`} alt="dopo"  className="absolute inset-0 w-full h-full object-contain" crossOrigin="use-credentials" />
            <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
              <img src={`${BACKEND_URL}${photoA.url}`} alt="prima" className="absolute inset-0 w-full h-full object-contain" style={{ width: `${(100 / pos) * 100}%` }} crossOrigin="use-credentials" />
            </div>
            {/* Divider */}
            <div className="absolute top-0 bottom-0 w-1 bg-white shadow-2xl cursor-ew-resize" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-xl">
                <GitCompare className="w-4 h-4 text-slate-900" />
              </div>
            </div>
            <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wider text-white px-2 py-0.5 rounded-full bg-emerald-500/80">PRIMA · {fmtDate(a.captured_at)}</span>
            <span className="absolute top-2 right-2 text-[10px] font-bold uppercase tracking-wider text-white px-2 py-0.5 rounded-full bg-violet-500/80">DOPO · {fmtDate(b.captured_at)}</span>
          </div>
        ) : (
          <p className="text-center text-sm text-slate-500 py-8">Nessuna foto disponibile per questa vista</p>
        )}

        <input
          type="range" min="0" max="100" value={pos}
          onChange={e => setPos(Number(e.target.value))}
          className="w-full accent-cyan-500 mt-1"
          data-testid="compare-range"
        />

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PostureTab;
